import React, { useContext, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { BasicType } from '../../interfaces/InterfacesLocal'
import { BasicNomenclator } from '../../interfaces/ServerInterfaces'
import { limpiarObjeto } from '../../utils/helpers'
import AsyncComboBox from '../forms/AsyncCombobox'
import ComboBox from '../forms/Combobox'
import DateInput from '../forms/DateInput'
import Input from '../forms/Input'
import MultiSelect from '../forms/Multiselect'
import Select from '../forms/Select'
import Toggle from '../forms/Toggle'
import Button from '../misc/Button'
import { FilterOpts, TableContext } from '../misc/GenericTable'
import MultipleFilterBtn from '../misc/MultipleFilterBtn'

interface FilterComponentInt {
  availableFilters: FilterOpts[]
  filterAction: (_data: BasicType | null) => void
}

interface FilterComponentInterface {
  filterComponent: FilterComponentInt | undefined
  filterActived: boolean
  setFilterActived: React.Dispatch<React.SetStateAction<boolean>>
  closeModal: () => void
}

const FilterComponent = ({
  filterComponent,
  filterActived,
  setFilterActived,
  closeModal,
}: FilterComponentInterface) => {
  const { selectedFilter, setSelectedFilter } = useContext(TableContext)

  const { handleSubmit, control, unregister, watch, reset } = useForm()
  const activeIndex = selectedFilter?.map((item) => item.id)
  const [selectedFilters, setSelectedFilters] = useState<BasicNomenclator[]>([])

  useEffect(() => {
    setSelectedFilters(
      selectedFilter?.map((item) => ({
        id: item.id.toString(),
        name: item.id.toString(),
      })) ?? []
    )
    const disabledFiltersCode = filterComponent?.availableFilters
      .filter((_, idx) => !activeIndex?.includes(idx))
      .map((item) => item.filterCode)
    if (selectedFilter?.length === 0 && filterActived) {
      filterComponent?.filterAction(null)
      setFilterActived(false)
    }
    unregister(disabledFiltersCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter])

  const resetForm = () => {
    if (setSelectedFilter) setSelectedFilter([])
    filterComponent?.filterAction(null)
    closeModal()
  }

  const onSubmit: SubmitHandler<BasicType> = (data) => {
    const dataToSend = limpiarObjeto(data)

    const findFilterItem = (filterCode: string) => {
      return filterComponent?.availableFilters?.find((item) => {
        if (item.format === 'datepicker-range') {
          return item?.datepickerRange?.some(
            (newItem) => newItem?.filterCode === filterCode
          )
        }
        if (item.format === 'price-range') {
          return item?.priceRange?.some(
            (price) => price?.filterCode === filterCode
          )
        }
        return item.filterCode === filterCode
      })
    }

    const newSelectedFilter = Object.entries(dataToSend).map(([id, name]) => {
      const filterItem = findFilterItem(id)
      const code = filterItem?.filterCode
      return {
        id,
        name,
        description: filterItem?.format ?? '',
        value: code ? dataToSend[code] : undefined,
      }
    })
    filterComponent?.filterAction(dataToSend)
    if (setSelectedFilter) setSelectedFilter(newSelectedFilter)
    closeModal()
    setFilterActived(true)
  }
  return (
    <form
      className="flex w-full flex-col gap-5"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/**ACTIONS BUTTONS */}
      <div className="flex w-full items-center justify-center">
        <MultipleFilterBtn
          btnName="Criterios de búsqueda"
          data={
            filterComponent?.availableFilters.map((item) => ({
              id: item.filterCode,
              name: item.name,
            })) ?? []
          }
          onClear={() => {
            if (selectedFilters.length > 0) {
              reset()
              setSelectedFilters(() => [])
            }
          }}
          selected={selectedFilters}
          setSelected={(data: BasicNomenclator[], unregistered?: string) => {
            setSelectedFilters(data)
            if (unregistered) unregister(unregistered)
          }}
        />
      </div>

      {/**FILTERS BODY */}
      <div className="flex flex-1 flex-col gap-3 px-1">
        {filterComponent?.availableFilters
          ?.filter((item) =>
            selectedFilters.some((s) => s.id === item.filterCode)
          )
          .map((item) =>
            item.format === 'select' && item.asyncData ? (
              <AsyncComboBox
                control={control}
                dataQuery={{
                  url: item.asyncData.url,
                  defaultParams: item.asyncData?.defaultParams,
                }}
                dependendValue={
                  item.dependentOn
                    ? { [item.dependentOn]: watch(item.dependentOn) }
                    : undefined
                }
                key={item.filterCode}
                label={item.name}
                name={item.filterCode}
                normalizeData={{
                  id: item.asyncData.idCode,
                  name: item.asyncData.dataCode,
                }}
                // defaultItem={selectedFilter?.find(elem => elem.id === item.filterCode)?.name ?? null}
              />
            ) : item.format === 'select' ? (
              <ComboBox
                control={control}
                data={item.data ?? []}
                defaultValue={
                  selectedFilter?.find((elem) => elem.id === item.filterCode)
                    ?.name ?? null
                }
                key={item.filterCode}
                label={item.name}
                name={item.filterCode}
                // rules={{ required: "Seleccione" }}
              />
            ) : item.format === 'multiselect' ? (
              <MultiSelect
                byDefault={(() => {
                  const found = selectedFilter?.find(
                    (elem) => elem.id === item.filterCode
                  )
                  const val = found?.value as unknown
                  return Array.isArray(val)
                    ? (val as (string | number | null)[])
                    : []
                })()}
                control={control}
                data={item.data ?? []}
                key={item.filterCode}
                label={item.name}
                name={item.filterCode}

                // rules={{ required: "Seleccione" }}
              />
            ) : item.format === 'input' ? (
              <Input
                control={control}
                defaultValue={
                  selectedFilter?.find((elem) => elem.id === item.filterCode)
                    ?.name ?? null
                }
                key={item.filterCode}
                label={item.name}
                name={item.filterCode}
              />
            ) : item.format === 'number' ? (
              <Input
                control={control}
                defaultValue={
                  selectedFilter?.find((elem) => elem.id === item.filterCode)
                    ?.name ?? null
                }
                key={item.filterCode}
                label={item.name}
                name={item.filterCode}
                type={'number'}
              />
            ) : item.format === 'boolean' ? (
              <Toggle
                control={control}
                defaultValue={
                  selectedFilter?.find((elem) => elem.id === item.filterCode)
                    ?.name ?? null
                }
                key={item.filterCode}
                name={item.filterCode}
                title={item.name}
              />
            ) : item.format === 'datepicker-range' ? (
              <div
                className="flex w-full flex-col items-start justify-start"
                key={item.filterCode}
              >
                <h2 className="mb-1 inline-flex items-center text-sm font-medium text-gray-700 first-letter:uppercase">
                  {item.name}
                </h2>
                <div
                  className="flex w-full items-center gap-1 font-normal"
                  key={`${item.filterCode}-container`}
                >
                  {item.datepickerRange?.map((rangeDatePicker) => {
                    return (
                      <div
                        className={'w-full'}
                        key={rangeDatePicker.filterCode}
                      >
                        <DateInput
                          control={control}
                          defaultValue={
                            selectedFilter?.find(
                              (elem) => elem.id === rangeDatePicker.filterCode
                            )?.name ?? null
                          }
                          fromCustom={
                            selectedFilter?.find(
                              (elem) => elem.id === item.filterCode
                            )?.name ?? null
                          }
                          includeTime={rangeDatePicker.includingTime}
                          key={rangeDatePicker.filterCode}
                          label={rangeDatePicker.name}
                          name={rangeDatePicker.filterCode}
                          // rules={{ required: "Este campo es requerido" }
                          untilToday={rangeDatePicker.isUnitlToday}
                          widthFull={true}
                          // rules={{ required: "Este campo es requerido" }}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : item.format === 'price-range' ? (
              <div
                className="flex w-full flex-col items-start justify-start"
                key={item.filterCode}
              >
                <h2 className="mb-1 inline-flex items-center text-sm font-medium text-gray-700 first-letter:uppercase">
                  {item.name}
                </h2>

                <div
                  className="flex items-center gap-1 font-normal"
                  key={`${item.filterCode}-container`}
                >
                  {item.priceRange?.map((priceRange) => {
                    return priceRange.filterCode !== 'currency' ? (
                      <Input
                        control={control}
                        key={priceRange.filterCode}
                        label={priceRange.name}
                        name={priceRange.filterCode}
                        textAsNumber
                        // rules={{ required: "Este campo es requerido" }}
                        type="number"
                      />
                    ) : (
                      <Select
                        control={control}
                        data={
                          priceRange?.currencies?.map((item) => ({
                            id: item,
                            name: item,
                          })) ?? []
                        }
                        key={priceRange.filterCode}
                        label={priceRange.name}
                        name={priceRange.filterCode}
                        // rules={{ required: "Este campo es requerido" }}
                      />
                    )
                  })}
                </div>
              </div>
            ) : (
              item.format === 'datepicker' && (
                <div
                  className="flex items-center gap-1 font-normal"
                  key={item.filterCode}
                >
                  <DateInput
                    control={control}
                    defaultValue={
                      selectedFilter?.find(
                        (elem) => elem.id === item.filterCode
                      )?.name ?? null
                    }
                    fromCustom={
                      selectedFilter?.find(
                        (elem) => elem.id === item.filterCode
                      )?.name ?? null
                    }
                    label={item.name}
                    name={item.filterCode}
                    // rules={{ required: "Este campo es requerido" }}
                    untilToday={item?.isUntilToday}
                  />
                </div>
              )
            )
          )}
      </div>
      <div className={'flex w-full justify-end gap-5'}>
        <Button
          action={resetForm}
          name="Limpiar"
          outline
          textColor="customBlue"
        />
        <Button name="Aplicar" type="submit" />
      </div>
    </form>
  )
}

export default FilterComponent
